import { IcoRadio } from "assets/icons";
import { Fragment, InputHTMLAttributes, useEffect } from "react";

export interface RadioOption {
	label: React.ReactNode;
	value: string;
	status?: string;
	disabled?: boolean;
}

type TagRadioInputProps = InputHTMLAttributes<HTMLInputElement> & {
	label?: string;
	error?: string | Array<string>;
	className?: string;
	touched?: boolean;
	options: RadioOption[];
	isRequired?: boolean;
	layout?: "horizontal" | "vertical";
	submittedTagNumber?: string;
};

export default function TagRadioInput({
	name,
	label,
	value,
	onChange,
	options,
	className,
	touched,
	error,
	isRequired = true,
	layout = "horizontal",
	submittedTagNumber
}: TagRadioInputProps): JSX.Element {
	const submittedTag = submittedTagNumber ? options.find((opt) => opt.value === submittedTagNumber) : null;

	const isSubmittedTagBlocked = submittedTag?.status === "naoEncontrado";

	const hasSingleTag = options.length === 1;

	const hasMultipleTags = options.length > 1;

	const hasActiveTags = options.some((opt) => opt.status !== "naoEncontrado");

	const shouldShowBlockedMessage = submittedTag && isSubmittedTagBlocked && (!value || value === submittedTagNumber);

	useEffect(() => {
		if (submittedTagNumber && !value && onChange) {
			const submittedTagExists = options.some((opt) => opt.value === submittedTagNumber);

			if (submittedTagExists) {
				const event = {
					target: {
						name,
						value: submittedTagNumber
					}
				} as React.ChangeEvent<HTMLInputElement>;

				onChange(event);
			}
		}
	}, [submittedTagNumber, value, onChange, name, options]);

	return (
		<fieldset
			className={`container ${className || ""} ${
				layout === "horizontal" ? "flex flex-row gap-6 justify-start align-center" : "flex flex-col gap-2"
			}`}
		>
			<div className={layout === "vertical" ? "mb-2" : ""}>
				{label && <span className="font-medium">{label}</span>}
				{isRequired && <span className="text-feedback-negative-pure-500"> *</span>}
			</div>

			{shouldShowBlockedMessage && (
				<div className="text-warning-pure-500 text-sm mb-2 bg-feedback-alert-alerts/20 w-80 p-4 rounded-lg">
					{hasSingleTag && "A tag identificada não está mais associada a este veículo"}
					{hasMultipleTags &&
						hasActiveTags &&
						"A tag identificada não está mais associada a este veículo, porém existem outras tags associadas"}
					{hasMultipleTags && !hasActiveTags && "A tag identificada não está mais associada a este veículo"}
				</div>
			)}

			<div className={layout === "horizontal" ? "flex flex-row gap-6" : "flex flex-col gap-2 pl-1"}>
				{options.map((option) => (
					<Fragment key={option.value}>
						<input
							id={`${name}-${option.value}`}
							name={name}
							value={option.value}
							onChange={onChange}
							type="radio"
							checked={option.value === value}
							disabled={option.status === "naoEncontrado" || option.disabled}
							hidden
						/>

						<label
							htmlFor={`${name}-${option.value}`}
							className={`flex flex-row gap-2 items-center ${
								option.status === "naoEncontrado" || option.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
							} ${
								error && touched
									? "bg-feedback-negative-high-50 border-feedback-negative-pure-500 focus-visible:outline-feedback-negative-pure-500"
									: ""
							}`}
						>
							<IcoRadio filled={option.value === value} color={option.status === "naoEncontrado" ? "#6B7280" : undefined} />
							<span>
								{option.label}
								{option.status === "bloqueado" && " (bloqueado)"}
							</span>
						</label>
					</Fragment>
				))}
			</div>

			{error && touched && (
				<div className="white-space--pre-line mt-1 text-xs text-feedback-negative-pure-500">
					{Array.isArray(error) ? error.join("\n") : error}
				</div>
			)}
		</fieldset>
	);
}
